import React, { FC, useMemo } from 'react'
import {
  Box,
  Typography,
  Switch,
  Stack,
  Paper,
  Container,
  Divider,
  CircularProgress,
} from '@mui/material'
import { BackButton } from '../../components/molecules/BackButton'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../../contexts/ToastContext'
import {
  useReshapeSettingsQuery,
  useToggleReshapeSettingMutation,
} from '../../../../features/reshape'
import {
  useAlertSettingsQuery,
  useToggleAlertSettingMutation,
} from '../../../../features/alert'

/**
 * OCR設定ページ（TanStack Query版）
 */
export const OcrSettingsPage: FC = () => {
  const navigate = useNavigate()
  const { showToast } = useToast()

  // Query hooks
  const { data: reshapeData, isPending: isReshapeLoading } =
    useReshapeSettingsQuery()
  const { data: alertData, isPending: isAlertLoading } = useAlertSettingsQuery()

  // Mutation hooks
  const toggleReshapeMutation = useToggleReshapeSettingMutation()
  const toggleAlertMutation = useToggleAlertSettingMutation()

  const initialLoading = isReshapeLoading || isAlertLoading
  const loading =
    toggleReshapeMutation.isPending || toggleAlertMutation.isPending

  // 設定値をデータから取得
  const gaiyouActionSupplementActive = useMemo(() => {
    const gaiyouSetting = reshapeData?.reshape_types.find(
      (type) => type.label === 'GAIYOU_ACTION_SUPPLEMENT'
    )
    return gaiyouSetting?.user_setting ?? false
  }, [reshapeData])

  const jikaseizaiNgAlertActive = useMemo(() => {
    const jikaseizaiSetting = alertData?.alert_types.find(
      (type) => type.label === 'INSURANCE_JIKASEIZAI_NG_ALTERNATE_EXISTS'
    )
    return jikaseizaiSetting?.user_setting ?? false
  }, [alertData])

  // 処方変換: 用法補足の切り替え
  const handleGaiyouActionSupplementChange = async (checked: boolean) => {
    try {
      const response = await toggleReshapeMutation.mutateAsync({
        reshape_type_label: 'GAIYOU_ACTION_SUPPLEMENT',
        active: checked,
      })
      showToast(response.message, 'success')
    } catch (err) {
      console.error('Failed to update GAIYOU_ACTION_SUPPLEMENT:', err)
      showToast('処方変換: 用法補足の設定更新に失敗しました', 'error', 5000)
    }
  }

  // AIガイド: 自家製剤加算取得NGの切り替え
  const handleJikaseizaiNgAlertChange = async (checked: boolean) => {
    try {
      const response = await toggleAlertMutation.mutateAsync({
        alert_type_label: 'INSURANCE_JIKASEIZAI_NG_ALTERNATE_EXISTS',
        active: checked,
      })
      showToast(response.message, 'success')
    } catch (err) {
      console.error(
        'Failed to update INSURANCE_JIKASEIZAI_NG_ALTERNATE_EXISTS:',
        err
      )
      showToast(
        'AIガイド: 自家製剤加算取得NGの設定更新に失敗しました',
        'error',
        5000
      )
    }
  }

  // 初期ローディング中の表示
  if (initialLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            gap: 3,
          }}
        >
          <CircularProgress
            size={48}
            thickness={4}
            sx={{
              color: 'primary.main',
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%': {
                  opacity: 0.6,
                  transform: 'scale(0.95)',
                },
                '50%': {
                  opacity: 1,
                  transform: 'scale(1)',
                },
                '100%': {
                  opacity: 0.6,
                  transform: 'scale(0.95)',
                },
              },
            }}
          />
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontWeight: 500,
              letterSpacing: 0.5,
            }}
          >
            設定を読み込み中...
          </Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Stack spacing={3}>
        {/* OCR設定 */}
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'medium',
              color: 'primary.dark',
              mb: 3,
              pb: 1,
              borderBottom: '1px solid',
              borderBottomColor: 'primary.dark',
            }}
          >
            OCR設定
          </Typography>

          <Stack spacing={3}>
            {/* 処方変換: 用法補足 */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'medium', mb: 0.5 }}
                >
                  処方変換: 用法補足
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  外用薬の用法補足処理を有効/無効にします
                </Typography>
              </Box>
              <Switch
                color="primary"
                checked={gaiyouActionSupplementActive}
                onChange={(e) =>
                  handleGaiyouActionSupplementChange(e.target.checked)
                }
                disabled={loading}
              />
            </Stack>

            <Divider />

            {/* AIガイド: 自家製剤加算取得NG */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'medium', mb: 0.5 }}
                >
                  AIガイド: 自家製剤加算取得NG
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  自家製剤NG代替品存在アラートを有効/無効にします
                </Typography>
              </Box>
              <Switch
                color="primary"
                checked={jikaseizaiNgAlertActive}
                onChange={(e) =>
                  handleJikaseizaiNgAlertChange(e.target.checked)
                }
                disabled={loading}
              />
            </Stack>
          </Stack>
        </Paper>
      </Stack>

      {/* ナビゲーションボタン */}
      <BackButton
        text="処方箋画面へ戻る"
        onClick={() => navigate('/')}
        variant="topLeft"
      />

      <BackButton
        text="管理ポータルに戻る"
        onClick={() => navigate('/portal')}
      />
    </Container>
  )
}

export default OcrSettingsPage
